import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  getDocumentPurchaseLines,
  getPurchaseLineFeatures,
  getPurchaseLineTemplateOptions,
  type PurchaseLineDTO,
  type PurchaseLineFeatures,
  type PurchaseLineTemplateOption,
  updatePurchaseLineTemplate,
} from "../../api/documents";
import { formatCurrencyAmount } from "../../utils/formatters";
import { formatVatRate } from "../../utils/formatVatRate";
import {
  getChangedPurchaseLineTemplates,
  hasTemplateAssignmentChange,
} from "../../utils/purchaseLineTemplateChanges";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  documentId: string;
  companyId: string;
  currency: string;
  onTemplatesApplied?: () => void;
};

function formatQuantity(value: number | null): string {
  if (value === null || value === undefined) {
    return "—";
  }

  return value.toLocaleString("pl-PL", {
    maximumFractionDigits: 4,
  });
}

function cloneRows(rows: PurchaseLineDTO[]): PurchaseLineDTO[] {
  return rows.map((row) => ({ ...row }));
}

export default function DocumentPurchaseLinesTable({
  documentId,
  companyId,
  currency,
  onTemplatesApplied,
}: Props) {
  const { t } = useAppTranslation("common");

  const [rows, setRows] = useState<PurchaseLineDTO[]>([]);
  const [savedRows, setSavedRows] = useState<PurchaseLineDTO[]>([]);
  const [templates, setTemplates] = useState<PurchaseLineTemplateOption[]>([]);
  const [features, setFeatures] = useState<PurchaseLineFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [pendingSaveRows, setPendingSaveRows] = useState<PurchaseLineDTO[]>([]);
  const [successOpen, setSuccessOpen] = useState(false);

  const loadErrorMessage = t("Nie udało się pobrać wierszy faktury.");

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setRows([]);
        setSavedRows([]);
        setTemplates([]);
        setFeatures(null);
        setIsEditing(false);

        const featureFlags = await getPurchaseLineFeatures(companyId);

        if (!active) {
          return;
        }

        setFeatures(featureFlags);

        if (
          !featureFlags.usePurchLineTemplates &&
          !featureFlags.usePurchOrderMatching &&
          !featureFlags.useKSeFSettlementHandling
        ) {
          return;
        }

        const lineRows = await getDocumentPurchaseLines(companyId, documentId);

        if (!active) {
          return;
        }

        setRows(cloneRows(lineRows));
        setSavedRows(cloneRows(lineRows));

        if (lineRows.length === 0) {
          setTemplates([]);
          return;
        }

        if (!featureFlags.usePurchLineTemplates) {
          setTemplates([]);
          return;
        }

        const templateOptions = await getPurchaseLineTemplateOptions(companyId);

        if (!active) {
          return;
        }

        setTemplates(templateOptions);
      } catch (e) {
        console.error(e);

        if (!active) {
          return;
        }

        setError(e instanceof Error ? e.message : loadErrorMessage);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      active = false;
    };
  }, [companyId, documentId, loadErrorMessage]);

  const knownTemplateCodes = useMemo(
    () => new Set(templates.map((template) => template.code)),
    [templates]
  );

  const changedRows = useMemo(
    () => getChangedPurchaseLineTemplates(rows, savedRows),
    [rows, savedRows]
  );

  const hasEditableRows = useMemo(
    () =>
      Boolean(features?.usePurchLineTemplates) &&
      rows.some((row) => row.lineEditable),
    [features?.usePurchLineTemplates, rows]
  );

  const handleEdit = () => {
    setRows(cloneRows(savedRows));
    setError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setRows(cloneRows(savedRows));
    setPendingSaveRows([]);
    setSaveConfirmationOpen(false);
    setError(null);
    setIsEditing(false);
  };

  const handleTemplateChange = (lineSystemId: string, portalTemplateCode: string) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.systemId === lineSystemId ? { ...row, portalTemplateCode } : row
      )
    );
  };

  const handleSave = async (
    overwriteDimensions: boolean,
    rowsToSave: PurchaseLineDTO[]
  ) => {
    if (rowsToSave.length === 0) {
      setIsEditing(false);
      return;
    }

    setSaveConfirmationOpen(false);
    setPendingSaveRows([]);
    setSaving(true);
    setError(null);

    try {
      const results = await Promise.allSettled(
        rowsToSave.map(async (row) => ({
          systemId: row.systemId,
          result: await updatePurchaseLineTemplate(
            companyId,
            documentId,
            row.systemId,
            row.portalTemplateCode,
            overwriteDimensions
          ),
        }))
      );

      const successfulCodes = new Map<string, string>();
      let failedCount = 0;

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          successfulCodes.set(
            result.value.systemId,
            result.value.result.portalTemplateCode
          );
        } else {
          failedCount += 1;
          console.error(result.reason);
        }
      });

      const nextSavedRows = savedRows.map((row) => {
        const savedCode = successfulCodes.get(row.systemId);
        return savedCode === undefined
          ? row
          : { ...row, portalTemplateCode: savedCode };
      });

      const nextRows = rows.map((row) => {
        const savedCode = successfulCodes.get(row.systemId);
        return savedCode === undefined
          ? row
          : { ...row, portalTemplateCode: savedCode };
      });

      setSavedRows(cloneRows(nextSavedRows));
      setRows(cloneRows(nextRows));

      // Przypisanie szablonu w BC moze automatycznie wygenerowac / zmienic
      // MPK i wymiary. Odswiezamy je po kazdej co najmniej jednej
      // poprawnie zapisanej linii, rowniez przy czesciowym powodzeniu zapisu.
      if (successfulCodes.size > 0) {
        onTemplatesApplied?.();
      }

      if (failedCount > 0) {
        setError(t("Nie udało się przypisać szablonu do wiersza faktury."));
        return;
      }

      setIsEditing(false);
      setSuccessOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    // Tworzymy snapshot dokładnie w momencie kliknięcia Zapisz. Dzięki temu
    // mieszane operacje (np. usunięcie jednego szablonu i zmiana drugiego)
    // są oceniane jako jedna spójna paczka zmian.
    const rowsToSave = getChangedPurchaseLineTemplates(rows, savedRows);

    if (rowsToSave.length === 0) {
      setIsEditing(false);
      return;
    }

    if (!hasTemplateAssignmentChange(rowsToSave, savedRows)) {
      void handleSave(false, rowsToSave);
      return;
    }

    setPendingSaveRows(cloneRows(rowsToSave));
    setSaveConfirmationOpen(true);
  };

  return (
    <Paper
      sx={{
        p: 2,
        display:
          !loading &&
          features &&
          !features.usePurchLineTemplates &&
          !features.usePurchOrderMatching &&
          !features.useKSeFSettlementHandling
            ? "none"
            : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="subtitle1">{t("Wiersze faktury")}</Typography>

        {!loading && features?.usePurchLineTemplates && rows.length > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {!isEditing ? (
              <Button
                size="small"
                variant="outlined"
                disabled={!hasEditableRows}
                onClick={handleEdit}
              >
                {t("Edytuj")}
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={handleCancel}
                >
                  {t("Anuluj")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving || changedRows.length === 0}
                  onClick={handleSaveClick}
                  startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                  {t("Zapisz")}
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">{t("Pobieranie...")}</Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: rows.length > 0 ? 2 : 0 }}>
          {error}
        </Alert>
      )}

      {!loading && rows.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">
          {t(
            "Brak wierszy faktury. Wiersze pojawią się po przetworzeniu dokumentu przez E-Dokumenty."
          )}
        </Typography>
      )}

      {!loading && rows.length > 0 && (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{ minWidth: features?.usePurchLineTemplates ? 980 : 760 }}
          >
            <TableHead sx={{ backgroundColor: "grey.100" }}>
              <TableRow>
                <TableCell sx={{ minWidth: 240 }}>{t("Opis")}</TableCell>
                <TableCell align="right">{t("Ilość")}</TableCell>
                <TableCell>{t("J.m.")}</TableCell>
                <TableCell align="right">{t("Cena jedn.")}</TableCell>
                <TableCell align="right">{t("Kwota")}</TableCell>
                <TableCell align="right">{t("Stawka VAT")}</TableCell>
                {features?.usePurchLineTemplates && (
                  <TableCell sx={{ minWidth: 220 }}>{t("Szablon")}</TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => {
                const hasUnknownCurrentTemplate =
                  Boolean(row.portalTemplateCode) &&
                  !knownTemplateCodes.has(row.portalTemplateCode);

                return (
                  <TableRow
                    key={row.systemId}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "grey.50",
                      },
                    }}
                  >
                    <TableCell>{row.description || "—"}</TableCell>
                    <TableCell align="right">
                      {formatQuantity(row.quantity)}
                    </TableCell>
                    <TableCell>{row.unitOfMeasureCode || "—"}</TableCell>
                    <TableCell align="right">
                      {formatCurrencyAmount(row.directUnitCost)} {currency}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrencyAmount(row.amount)} {currency}
                    </TableCell>
                    <TableCell align="right">
                      {formatVatRate(row.vatPercent, row.vatProdPostingGroup)}
                    </TableCell>
                    {features?.usePurchLineTemplates && (
                      <TableCell>
                        {isEditing && row.lineEditable ? (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={row.portalTemplateCode}
                            disabled={saving}
                            onChange={(event) =>
                              handleTemplateChange(row.systemId, event.target.value)
                            }
                            aria-label={t("Szablon wiersza faktury")}
                          >
                            <MenuItem value="">{t("Brak szablonu")}</MenuItem>
                            {hasUnknownCurrentTemplate && (
                              <MenuItem value={row.portalTemplateCode}>
                                {row.portalTemplateCode}
                              </MenuItem>
                            )}
                            {templates.map((template) => (
                              <MenuItem key={template.code} value={template.code}>
                                {template.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <Typography
                            variant="body2"
                            color={
                              row.portalTemplateCode
                                ? "text.primary"
                                : "text.secondary"
                            }
                          >
                            {row.portalTemplateCode || t("Brak szablonu")}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={saveConfirmationOpen}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Nadpisać wymiary wierszy faktury?")}</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t(
              "Przypisanie szablonu może nadpisać aktualnie ustawione wymiary dla zmienianych wierszy faktury."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t(
              "Czy chcesz zastąpić obecną konfigurację wymiarów wartościami z wybranego szablonu?"
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void handleSave(false, pendingSaveRows)}
            disabled={saving}
          >
            {t("Nie, zachowaj wymiary")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave(true, pendingSaveRows)}
            disabled={saving}
          >
            {t("Tak, nadpisz wymiary")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={2500}
        onClose={() => setSuccessOpen(false)}
        message={t("Szablon wiersza faktury został zapisany.")}
      />
    </Paper>
  );
}
